import { ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { AgentLineCopyurl } from '@/app/components/icons/AgentLineCopyurl';
import { AgentLineFlie } from '@/app/components/icons/AgentLineFlie';
import { AgentLineScript } from '@/app/components/icons/AgentLineScript';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineDownload2 } from '@/app/components/icons/BaseLineDownload2';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineUp2 } from '@/app/components/icons/BaseLineUp2';
import { Button } from '@/app/components/ui/button';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/app/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { SITE } from '@/app/constants';
import type { ClawStatus } from '@/app/types';
import { cn, formatFileSize } from '@/app/utils';
import { normalizePath } from '../../../_utils';
import type { ClawFileItem } from '../../../types';
import type { ActiveSelection, DisplayRow } from '../types';
import { formatDate, getFolderKey, getItemName, isFolder } from '../utils';

interface FileTableProps {
  className?: string;
  clawStatus: ClawStatus | null;
  initialLoading: boolean;
  displayRows: DisplayRow[];
  activeSelection: ActiveSelection;
  expandedFolderMap: Record<string, boolean>;
  loadingFolderMap: Record<string, boolean>;
  deletingPath: string;
  creatingFolder: boolean;
  uploadingFile: boolean;
  watermarkDownloadingPath?: string;
  /**
   * 自定义表格列宽模板（CSS grid-template-columns）。
   * 必须保持 4 列：name / size / modified / action。
   */
  gridTemplateColumns?: string;
  onToggleFolder: (item: ClawFileItem) => void;
  onSelectFolder: (item: ClawFileItem) => void;
  onSelectFile: (item: ClawFileItem) => void;
  onUpload: (targetFolderPath?: string) => void;
  onCreateFolder: (targetFolderPath?: string) => void;
  onDownload: (item: ClawFileItem) => void;
  onWatermarkDownload?: (item: ClawFileItem) => void;
  onCopyPath: (item: ClawFileItem) => void;
  onDelete: (item: ClawFileItem) => void;
  onRefresh: (item: ClawFileItem) => void;
}

const DEFAULT_FILE_TABLE_GRID_TEMPLATE = 'minmax(0,1fr) 80px 90px 60px';

function formatSize(size?: string): string {
  const normalizedSize = size?.trim();
  if (!normalizedSize || normalizedSize === '-') return '-';

  const bytes = Number(normalizedSize);
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes === 0) return '0 B';

  const precision = bytes < 1024 ? 0 : 1;
  return formatFileSize(bytes, precision)?.replace(' Bytes', ' B') ?? '-';
}

interface FileRowActionMenuProps {
  item: ClawFileItem;
  isFolderItem: boolean;
  isDeleting: boolean;
  deletingPath: string;
  creatingFolder: boolean;
  uploadingFile: boolean;
  canManage: boolean;
  watermarkDownloadingPath?: string;
  onUpload: (targetFolderPath?: string) => void;
  onCreateFolder: (targetFolderPath?: string) => void;
  onDownload: (item: ClawFileItem) => void;
  onWatermarkDownload?: (item: ClawFileItem) => void;
  onCopyPath: (item: ClawFileItem) => void;
  onDelete: (item: ClawFileItem) => void;
  onRefresh: (item: ClawFileItem) => void;
}

interface FileRowMenuAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  isDanger?: boolean;
  onSelect: () => void;
}

const HOVER_CLOSE_DELAY = 200;

function useFileRowMenuActions(props: FileRowActionMenuProps & { onAfterSelect?: () => void }): FileRowMenuAction[] {
  const t = useTranslations();
  const {
    item,
    isFolderItem,
    isDeleting,
    deletingPath,
    creatingFolder,
    uploadingFile,
    canManage,
    watermarkDownloadingPath,
    onUpload,
    onCreateFolder,
    onDownload,
    onWatermarkDownload,
    onCopyPath,
    onDelete,
    onRefresh,
    onAfterSelect,
  } = props;
  const itemPath = normalizePath(item.path);
  const isWatermarkDownloading = watermarkDownloadingPath === itemPath;
  const actions: FileRowMenuAction[] = [];

  if (isFolderItem && canManage) {
    actions.push({
      key: 'upload',
      label: t('claw.file.upload'),
      icon: <BaseLineUp2 className="size-4" />,
      disabled: uploadingFile,
      onSelect: () => {
        onUpload(itemPath);
        onAfterSelect?.();
      },
    });
    actions.push({
      key: 'new-folder',
      label: t('claw.file.newFolder'),
      icon: <BaseLinePlus className="size-4" />,
      disabled: creatingFolder,
      onSelect: () => {
        onCreateFolder(itemPath);
        onAfterSelect?.();
      },
    });
  }

  actions.push({
    key: 'copy',
    label: t('claw.file.table.copy'),
    icon: <AgentLineCopyurl className="size-4" />,
    onSelect: () => {
      onCopyPath(item);
      onAfterSelect?.();
    },
  });

  if (!isFolderItem) {
    if (SITE === 'china' && onWatermarkDownload) {
      actions.push({
        key: 'download-watermark',
        label: t('claw.assets.actions.downloadWithWatermark'),
        icon: isWatermarkDownloading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <BaseLineDownload2 className="size-4" />
        ),
        disabled: isWatermarkDownloading,
        onSelect: () => {
          onWatermarkDownload(item);
          onAfterSelect?.();
        },
      });
      actions.push({
        key: 'download-no-watermark',
        label: t('claw.assets.actions.downloadNoWatermark'),
        icon: <BaseLineDownload2 className="size-4" />,
        onSelect: () => {
          onDownload(item);
          onAfterSelect?.();
        },
      });
    } else {
      actions.push({
        key: 'download',
        label: t('claw.assets.actions.download'),
        icon: <BaseLineDownload2 className="size-4" />,
        onSelect: () => {
          onDownload(item);
          onAfterSelect?.();
        },
      });
    }
  }

  actions.push({
    key: 'refresh',
    label: t('claw.file.table.refresh'),
    icon: <RefreshCw className="size-4" />,
    onSelect: () => {
      onRefresh(item);
      onAfterSelect?.();
    },
  });

  actions.push({
    key: 'delete',
    label: t('claw.nav.delete'),
    icon: isDeleting ? <Loader2 className="size-4 animate-spin" /> : <BaseLineDelete className="size-4 text-inherit" />,
    disabled: !!deletingPath,
    isDanger: true,
    onSelect: () => {
      onDelete(item);
      onAfterSelect?.();
    },
  });

  return actions;
}

function MenuActionItems({ actions, Item }: { actions: FileRowMenuAction[]; Item: React.ElementType }) {
  return actions.map((action) => (
    <Item
      key={action.key}
      className={cn(
        'flex items-center gap-2',
        action.isDanger && 'text-[#F04141] focus:bg-[rgba(240,65,65,0.24)] focus:text-[#F04141]',
      )}
      disabled={action.disabled}
      onSelect={action.onSelect}
    >
      {action.icon}
      {action.label}
    </Item>
  ));
}

/**
 * 同时提供右键上下文菜单和 hover 操作按钮（dropdown）。
 * 通过 render prop children 将 dropdown 按钮注入到行布局的指定位置。
 */
function FileRowMenu({
  children,
  item,
  isFolderItem,
  isDeleting,
  deletingPath,
  creatingFolder,
  uploadingFile,
  canManage,
  watermarkDownloadingPath,
  onUpload,
  onCreateFolder,
  onDownload,
  onWatermarkDownload,
  onCopyPath,
  onDelete,
  onRefresh,
  onSelect,
}: FileRowActionMenuProps & { children: (actionButton: React.ReactNode) => React.ReactNode; onSelect?: () => void }) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const actions = useFileRowMenuActions({
    item,
    isFolderItem,
    isDeleting,
    deletingPath,
    creatingFolder,
    uploadingFile,
    canManage,
    watermarkDownloadingPath,
    onUpload,
    onCreateFolder,
    onDownload,
    onWatermarkDownload,
    onCopyPath,
    onDelete,
    onRefresh,
    onAfterSelect: () => setOpen(false),
  });

  const clearCloseTimer = () => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const handleOpen = () => {
    clearCloseTimer();
    setOpen(true);
  };
  const handleDelayedClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const actionButton = (
    <DropdownMenu
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        clearCloseTimer();
        setOpen(nextOpen);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="text"
          size="sm"
          className="rounded-8 p-2 text-system-white64 transition-colors hover:bg-system-white08 hover:text-system-white"
          title={t('claw.file.table.action')}
          onMouseEnter={handleOpen}
          onMouseLeave={handleDelayedClose}
        >
          <BaseLineMore className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="min-w-[148px] rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.88)] light:bg-system-bg02 p-1 text-system-text01 backdrop-blur-[30px]"
        onMouseEnter={handleOpen}
        onMouseLeave={handleDelayedClose}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <MenuActionItems actions={actions} Item={DropdownMenuItem} />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (open) onSelect?.();
      }}
    >
      <ContextMenuTrigger asChild>{children(actionButton)}</ContextMenuTrigger>
      <ContextMenuContent className="min-w-[148px] rounded-12 border border-system-white08 bg-[rgba(34,37,42,0.88)] light:bg-system-bg02 p-1 text-system-text01 backdrop-blur-[30px]">
        <MenuActionItems actions={actions} Item={ContextMenuItem} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

export const FileTable = ({
  clawStatus,
  className,
  initialLoading,
  displayRows,
  activeSelection,
  expandedFolderMap,
  loadingFolderMap,
  deletingPath,
  creatingFolder,
  uploadingFile,
  watermarkDownloadingPath,
  gridTemplateColumns = DEFAULT_FILE_TABLE_GRID_TEMPLATE,
  onToggleFolder,
  onSelectFolder,
  onSelectFile,
  onUpload,
  onCreateFolder,
  onDownload,
  onWatermarkDownload,
  onCopyPath,
  onDelete,
  onRefresh,
}: FileTableProps) => {
  const t = useTranslations();
  const canManage = clawStatus === 'CLAW_STATUS_ACTIVE';

  return (
    <div
      className={cn(
        'flex size-full flex-col overflow-hidden rounded-12 bg-[rgba(0,0,0,0.24)] light:bg-system-white04 px-4 py-3',
        className,
      )}
    >
      <div className="grid px-4 py-2 text-system-white40 text-xs" style={{ gridTemplateColumns }}>
        <div className="flex items-center gap-1">{t('claw.file.table.name')}</div>
        <div>{t('claw.file.table.size')}</div>
        <div>{t('claw.file.table.modified')}</div>
        <div className="text-right">{t('claw.file.table.action')}</div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {initialLoading ? (
          <div className="flex h-full items-center justify-center text-system-white40">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : displayRows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-system-white40">
            {t('claw.file.empty')}
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {displayRows.map(({ item, depth }) => {
              const itemPath = normalizePath(item.path);
              const itemKey = getFolderKey(itemPath);
              const folderExpanded = !!expandedFolderMap[itemKey];
              const folderLoading = !!loadingFolderMap[itemKey];
              const isFolderItem = isFolder(item);
              const itemName = getItemName(item);
              const isDeleting = deletingPath === itemPath;
              const isActiveFolder =
                isFolderItem && activeSelection?.type === 'folder' && itemPath === activeSelection.path;
              const isActiveFile =
                !isFolderItem && activeSelection?.type === 'file' && itemPath === activeSelection.path;
              const sizeText = isFolderItem ? '-' : formatSize(item.size);

              return (
                <FileRowMenu
                  key={itemPath || `${itemName}-${depth}`}
                  item={item}
                  isFolderItem={isFolderItem}
                  isDeleting={isDeleting}
                  deletingPath={deletingPath}
                  creatingFolder={creatingFolder}
                  uploadingFile={uploadingFile}
                  canManage={canManage}
                  watermarkDownloadingPath={watermarkDownloadingPath}
                  onUpload={onUpload}
                  onCreateFolder={onCreateFolder}
                  onDownload={onDownload}
                  onWatermarkDownload={onWatermarkDownload}
                  onCopyPath={onCopyPath}
                  onDelete={onDelete}
                  onRefresh={onRefresh}
                  onSelect={() => {
                    const isAlreadySelected = isFolderItem ? isActiveFolder : isActiveFile;
                    if (!isAlreadySelected) {
                      isFolderItem ? onSelectFolder(item) : onSelectFile(item);
                    }
                  }}
                >
                  {(actionButton) => (
                    <div
                      className={cn(
                        'grid cursor-pointer items-center rounded-12 px-4 py-1.5',
                        isActiveFolder && 'bg-system-white08',
                        isActiveFile && 'bg-system-white16',
                        !isActiveFile && 'hover:bg-system-white04',
                      )}
                      style={{ gridTemplateColumns }}
                      onClick={() => (isFolderItem ? onToggleFolder(item) : onSelectFile(item))}
                    >
                      <div className="flex min-w-0 items-center gap-3 py-2" style={{ paddingLeft: `${depth * 20}px` }}>
                        {isFolderItem ? (
                          <button
                            type="button"
                            className="flex size-4 items-center justify-center text-system-white64"
                            onClick={(event) => {
                              event.stopPropagation();
                              onToggleFolder(item);
                            }}
                          >
                            {folderLoading ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <ChevronRight
                                className={cn('size-4 transition-transform', folderExpanded && 'rotate-90')}
                              />
                            )}
                          </button>
                        ) : (
                          <span className="block size-4" />
                        )}

                        {isFolderItem ? (
                          <AgentLineFlie className="size-5 shrink-0 text-system-white80" />
                        ) : (
                          <AgentLineScript className="size-5 shrink-0 text-system-white80" />
                        )}

                        <div className="w-full min-w-0 text-left">
                          <p className="break-all text-sm text-system-white">{itemName}</p>
                        </div>
                      </div>

                      <div className="text-system-white40 text-xs">{sizeText}</div>
                      <div className="text-system-white40 text-xs">
                        {isFolderItem ? '-' : formatDate(item.last_modified)}
                      </div>
                      <div className="flex items-center justify-end" onClick={(event) => event.stopPropagation()}>
                        {actionButton}
                      </div>
                    </div>
                  )}
                </FileRowMenu>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
